import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  FilterLiveSearch,
  DateInput,
  BooleanInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Chip } from '@mui/material';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';
import { PageWrapper } from '../../components/PageWrapper';

const StatusField = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  
  if (!record) return null;
  
  const isActive = !record.returnedDate;
  
  return (
    <Chip 
      label={translate(isActive ? 'resources.equipment_assignments.status.active' : 'resources.equipment_assignments.status.returned', {
        defaultValue: isActive ? 'Active' : 'Returned'
      })} 
      color={isActive ? 'success' : 'default'}
      size="small"
      variant={isActive ? 'filled' : 'outlined'}
    />
  );
};

const DurationField = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  
  if (!record?.assignedDate) return null;
  
  const start = new Date(record.assignedDate);
  const end = record.returnedDate ? new Date(record.returnedDate) : new Date();
  const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  return (
    <span>
      {days} {translate('resources.equipment_assignments.fields.days', { defaultValue: 'days' })}
    </span>
  );
};

const MileageDifferenceField = () => {
  const record = useRecordContext();
  
  if (!record?.mileageOut || !record?.mileageIn) return <span>-</span>;
  
  const difference = record.mileageIn - record.mileageOut;
  
  return (
    <span>{difference.toLocaleString()}</span>
  );
};

const AssignmentEmptyState = createEmptyStateComponent('equipment-assignments');

const AssignmentListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const useAssignmentFilters = () => {
  return React.useMemo(
    () => [
      <FilterLiveSearch key="notes" source="notes" placeholder="Search assignments..." />,
      <DateInput key="assignedDate" source="assignedDate" label="Assigned Date" />,
      <BooleanInput key="active" source="active" label="Active Only" transform={v => v ? { returnedDate: null } : undefined} />,
    ],
    []
  );
};

export const EquipmentAssignmentList = () => {
  const translate = useTranslate();
  const filters = useAssignmentFilters();
  
  return (
    <PageWrapper title="Equipment Assignments">
      <List
        perPage={25}
        sort={{ field: 'assignedDate', order: 'DESC' }}
        filters={filters}
        actions={<AssignmentListActions />}
        empty={<AssignmentEmptyState />}
      >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
          <TextField source="id" sortable={false} />
          <ReferenceField source="equipmentId" reference="equipment">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="driverId" reference="drivers" emptyText="-">
            <TextField source="name" />
          </ReferenceField>
          <DateField source="assignedDate" />
          <DateField source="returnedDate" emptyText="-" />
          <StatusField />
          <DurationField />
          <NumberField source="mileageOut" emptyText="-" />
          <NumberField source="mileageIn" emptyText="-" />
          <MileageDifferenceField />
          <TextField source="condition" emptyText="-" />
          <DateField source="createdAt" />
        </Datagrid>
      </List>
    </PageWrapper>
  );
};